import React from "react";
import {
  COMPANIES, PERSONS, COMPANY_ORDER, PERSON_ORDER, CATEGORIES, STEPS, ddmmyyyy, formatRupiah, suggestCategoryFromKbli,
} from "./data";
import {
  activeVersion, totalSharesBig, directFrac, effective, personDirectCapital, EffCache, Flag,
} from "./model";
import {
  Frac, makeFrac, mulF, toPercentString, toFractionString, greaterThanPercent, isZero,
} from "./rational";
import { fileUrl } from "./api";

interface Props {
  sel: { kind: "person" | "company"; id: string };
  step: number;
  cache: EffCache;
  flags: Flag[];
  companyCat: Record<string, string>;
  onChangeCat: (companyId: string, cat: string) => void;
  companyOps: Record<string, string>;
  onChangeOps: (companyId: string, val: string) => void;
  primaryKbli: Record<string, string>;
  onChangePrimaryKbli: (companyId: string, code: string) => void;
  docExtra: Record<string, Record<string, { note?: string; url?: string }>>;
  onDocExtra: (companyId: string, docKey: string, patch: { note?: string; url?: string }) => void;
  onClose: () => void;
  onSelectEntity: (kind: "person" | "company", id: string) => void;
  canEdit?: boolean;
  onUploadForCompany?: (companyId: string) => void;
}

// enumerasi jalur kepemilikan orang -> PT (edge = holder -> company)
function ownershipPaths(personId: string, companyId: string, step: number) {
  const results: { chain: string[]; fracs: Frac[]; product: Frac }[] = [];
  const stack = new Set<string>();
  function dfs(current: string, chain: string[], fracs: Frac[], product: Frac) {
    if (current === companyId) {
      results.push({ chain: [...chain], fracs: [...fracs], product });
      return;
    }
    // current is an owner; find companies it owns that lead toward companyId
    for (const cid of COMPANY_ORDER) {
      const v = activeVersion(cid, step);
      if (!v) continue;
      const shares = v.holders.filter((h) => h[0] === current).reduce((s, h) => s + h[1], 0);
      if (shares === 0) continue;
      if (stack.has(cid)) continue;
      stack.add(cid);
      const f = makeFrac(BigInt(shares), totalSharesBig(v));
      dfs(cid, [...chain, cid], [...fracs, f], mulF(product, f));
      stack.delete(cid);
    }
  }
  dfs(personId, [personId], [], makeFrac(1n, 1n));
  return results;
}

export default function DetailPanel(props: Props) {
  const { sel, step, cache, flags, companyCat, onChangeCat, onClose } = props;
  return (
    <div className={"rp open"} data-testid="detail-panel">
      {sel.kind === "company"
        ? <CompanyDetail {...props} />
        : <PersonDetail {...props} />}
    </div>
  );
}

function Header({ title, sub, onClose }: { title: string; sub?: string; onClose: () => void }) {
  return (
    <div className="rp-head">
      <button className="rp-close" onClick={onClose} data-testid="close-panel">×</button>
      <div className="rp-title serif">{title}</div>
      {sub && <div className="rp-sub">{sub}</div>}
    </div>
  );
}
function Src({ text }: { text?: string }) {
  if (!text) return null;
  return <div className="src">Sumber: {text}</div>;
}

function CompanyDetail(props: Props) {
  const { sel, step, cache, flags, companyCat, onChangeCat, companyOps, onChangeOps, primaryKbli, onChangePrimaryKbli, docExtra, onDocExtra, onClose } = props;
  const canEdit = props.canEdit !== false;
  const c = COMPANIES[sel.id];
  const documentedMain = c.kbli.find((k) => k.main)?.code;
  const effectivePrimary = primaryKbli[sel.id] ?? documentedMain;
  const v = activeVersion(sel.id, step);
  const dateIso = props.step >= 0 ? undefined : undefined;
  const total = v ? totalSharesBig(v) : 0n;

  // agregasi holder
  const holderAgg = new Map<string, number>();
  if (v) for (const [id, s] of v.holders) holderAgg.set(id, (holderAgg.get(id) || 0) + s);

  const sumShares = v ? Array.from(holderAgg.values()).reduce((a, b) => a + b, 0) : 0;
  const calc = v ? sumShares * v.nominal : 0;
  const cocok = v ? calc === v.ditempatkan : false;

  // efektif perorangan
  const effList: { id: string; frac: Frac }[] = [];
  for (const pid of PERSON_ORDER) {
    const f = effective(pid, sel.id, cache);
    if (!isZero(f)) effList.push({ id: pid, frac: f });
  }

  const companyFlags = flags.filter((f) => f.companyId === sel.id);
  const stepDate = ddmmyyyy(dataStepDate(step));

  return (
    <>
      <Header title={c.name} sub={`Kedudukan ${c.kedudukan}. Data per ${stepDate}.`} onClose={onClose} />
      <div className="rp-body">
        {!v ? (
          <div className="sec"><div className="rp-sub">PT ini belum berdiri pada tanggal yang dipilih.</div></div>
        ) : (
          <>
            <div className="sec">
              <h5>Kepemilikan saham</h5>
              <table className="kv">
                <thead><tr><th>Pemegang</th><th className="num">Lembar</th><th className="num">Nominal</th><th className="num">%</th></tr></thead>
                <tbody>
                  {Array.from(holderAgg.entries()).map(([id, s]) => {
                    const f = makeFrac(BigInt(s), total);
                    return (
                      <tr key={id}>
                        <td>{PERSONS[id]?.name ?? COMPANIES[id]?.name ?? id}</td>
                        <td className="num">{s.toLocaleString("id-ID")}</td>
                        <td className="num">{formatRupiah(s * v.nominal)}</td>
                        <td className="num">{toPercentString(f)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {cocok ? (
                <div className="check-ok">✓ {sumShares.toLocaleString("id-ID")} lembar × {formatRupiah(v.nominal)} = {formatRupiah(calc)} sama dengan modal ditempatkan</div>
              ) : (
                <div className="check-bad">✗ {sumShares.toLocaleString("id-ID")} lembar × {formatRupiah(v.nominal)} = {formatRupiah(calc)} tidak sama dengan modal ditempatkan {formatRupiah(v.ditempatkan)}</div>
              )}
              <Src text={v.srcHold} />
            </div>

            <div className="sec">
              <h5>Kepemilikan efektif perorangan</h5>
              <table className="kv">
                <thead><tr><th>Orang</th><th className="num">%</th><th className="num">Pecahan</th></tr></thead>
                <tbody>
                  {effList.map(({ id, frac }) => (
                    <tr key={id}>
                      <td>{PERSONS[id].name}{greaterThanPercent(frac, 25) ? <span className="badge info">UBO?</span> : null}</td>
                      <td className="num">{toPercentString(frac)}</td>
                      <td className="num">{toFractionString(frac)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="sec">
              <h5>Pengurus</h5>
              <table className="kv"><tbody>
                {v.officers.map(([pid, role], i) => (
                  <tr key={i}><td>{role}</td><td>{PERSONS[pid]?.name ?? pid}</td></tr>
                ))}
              </tbody></table>
              {v.term && <div className="rp-sub" style={{ marginTop: 6 }}>{v.term}</div>}
            </div>

            <div className="sec">
              <h5>Modal</h5>
              <table className="kv"><tbody>
                <tr><td>Modal dasar</td><td className="num">{formatRupiah(v.dasar)}</td><td className="num">{(v.dasar / v.nominal).toLocaleString("id-ID")} lembar</td></tr>
                <tr><td>Modal ditempatkan</td><td className="num">{formatRupiah(v.ditempatkan)}</td><td className="num">{(v.ditempatkan / v.nominal).toLocaleString("id-ID")} lembar</td></tr>
                <tr><td>Nominal / lembar</td><td className="num">{formatRupiah(v.nominal)}</td><td className="num">—</td></tr>
              </tbody></table>
            </div>

            <div className="sec">
              <h5>Domisili &amp; pajak</h5>
              <table className="kv"><tbody>
                <tr><td>Kedudukan</td><td>{c.kedudukan}</td></tr>
                <tr><td>Alamat</td><td>{c.alamat ?? <span className="sev-yellow">Belum ada dokumen</span>}</td></tr>
                <tr><td>Domisili operasional</td><td>
                  <input className="edit-inp" value={companyOps[sel.id] ?? c.operasional ?? ""}
                    placeholder="Isi manual bila perlu…" disabled={!canEdit}
                    onChange={(e) => onChangeOps(sel.id, e.target.value)} data-testid="ops-input" />
                  <span className="badge">manual</span>
                </td></tr>
                <tr><td>KPP</td><td>{c.kpp ?? "—"}{c.kppNote ? <span className="badge">{c.kppNote}</span> : null}</td></tr>
                <tr><td>NPWP</td><td data-testid="company-npwp">{c.npwp ?? "—"}</td></tr>
                <tr><td>Kategori</td><td>
                  <select className="pill-sel" value={companyCat[sel.id]} disabled={!canEdit} onChange={(e) => onChangeCat(sel.id, e.target.value)} data-testid="cat-select">
                    {Object.keys(CATEGORIES).map((k) => <option key={k} value={k}>{CATEGORIES[k].name}</option>)}
                  </select>
                  {(() => {
                    const suggested = suggestCategoryFromKbli(effectivePrimary || "");
                    if (!suggested || !CATEGORIES[suggested]) return null;
                    const cur = companyCat[sel.id];
                    if (cur === suggested)
                      return <div className="cat-suggest ok" data-testid="cat-suggest">✓ Sesuai KBLI utama ({CATEGORIES[suggested].name})</div>;
                    return (
                      <div className="cat-suggest" data-testid="cat-suggest">
                        Saran dari KBLI utama: <b>{CATEGORIES[suggested].name}</b>
                        <button className="mini-btn" disabled={!canEdit} onClick={() => onChangeCat(sel.id, suggested)} data-testid="apply-cat-suggestion">Pakai saran</button>
                      </div>
                    );
                  })()}
                  {!effectivePrimary && <div className="cat-suggest muted" data-testid="cat-suggest-hint">Pilih KBLI utama di bawah untuk melihat saran kategori.</div>}
                </td></tr>
              </tbody></table>
              <Src text={c.alamatSrc} />
            </div>

            <div className="sec">
              <h5>KBLI</h5>
              <div className="rp-sub" style={{ marginBottom: 6 }}>Pilih satu sebagai KBLI utama (radio). Semua KBLI dari NIB tetap tersimpan.</div>
              <table className="kv"><tbody>
                {c.kbli.map((k, idx) => {
                  const isPrimary = effectivePrimary === k.code;
                  return (
                    <tr key={k.code}>
                      <td style={{ width: 22 }}>
                        <input type="radio" name={`kbli-${sel.id}`} checked={isPrimary} disabled={!canEdit}
                          onChange={() => onChangePrimaryKbli(sel.id, k.code)} data-testid={`kbli-radio-${idx}`} title="Jadikan KBLI utama" />
                      </td>
                      <td className="num" style={{ paddingRight: 10 }}>{k.code}</td>
                      <td>{k.title}
                        {isPrimary && <span className="badge info">utama</span>}
                        {k.main && <span className="badge">dari NIB</span>}
                        {k.unverified && <span className="badge">dari bagan lama</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody></table>
              <Src text={c.kbliSrc} />
            </div>

            <div className="sec">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <h5 style={{ margin: 0 }}>Dokumen (arsip)</h5>
                {canEdit && props.onUploadForCompany && (
                  <button className="mini-btn" onClick={() => props.onUploadForCompany!(sel.id)} data-testid={`upload-susulan-docs-${sel.id}`} title="Unggah dokumen susulan, AI melengkapi data">+ Susulkan dokumen</button>
                )}
              </div>
              <div className="rp-sub" style={{ margin: "6px 0 8px" }}>Syarat minimal: Akta Pendirian, Akta Perubahan (jika ada), NIB, NPWP.</div>
              {c.documents.map((d, i) => {
                const dk = `${d.kind}-${i}`;
                const manual = docExtra[sel.id]?.[dk];
                const url = d.url ?? (d.storagePath ? fileUrl(d.storagePath) : (manual?.url || ""));
                const ada = !!url;
                return (
                  <div className="doc-item" key={dk} data-testid={`doc-${sel.id}-${dk}`}>
                    <div className="doc-row">
                      <span className={"doc-badge " + (ada ? "ok" : "missing")}>{ada ? "Ada" : "Belum ada"}</span>
                      <span className="doc-label">{d.label}{ada && d.inBundle ? <span className="rp-sub"> · {d.inBundle}</span> : null}</span>
                      {ada && <a className="doc-link" href={url} target="_blank" rel="noreferrer" data-testid={`doc-view-${sel.id}-${dk}`}>Lihat dokumen asli ↗</a>}
                    </div>
                    {!ada && (
                      <input className="edit-inp doc-inp" placeholder="Tempel link dokumen (opsional)" value={manual?.url ?? ""} disabled={!canEdit}
                        onChange={(e) => onDocExtra(sel.id, dk, { url: e.target.value })} data-testid={`doc-url-${sel.id}-${dk}`} />
                    )}
                    <input className="edit-inp doc-inp" placeholder="Catatan (opsional)" value={manual?.note ?? ""} disabled={!canEdit}
                      onChange={(e) => onDocExtra(sel.id, dk, { note: e.target.value })} data-testid={`doc-note-${sel.id}-${dk}`} />
                  </div>
                );
              })}
            </div>

            <div className="sec">
              <h5>Histori akta</h5>
              {c.history.map((h, i) => (
                <div key={i} className={"histline" + (h.step > step ? " after" : "")}>
                  <div className="ht">{ddmmyyyy(dataStepDate(h.step))} — {h.t}{h.step > step ? " (setelah tanggal yang dipilih)" : ""}</div>
                  <div className="hp">{h.p}</div>
                  <Src text={h.s} />
                  {h.docUrl && <a className="doc-link hist-doc" href={h.docUrl} target="_blank" rel="noreferrer" data-testid={`hist-doc-${sel.id}-${h.step}`}>Lihat dokumen asli ↗</a>}
                </div>
              ))}
            </div>

            <div className="sec">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <h5 style={{ margin: 0 }}>Data Issues</h5>
                {canEdit && props.onUploadForCompany && companyFlags.some((f) => /DOK/i.test(f.code)) && (
                  <button className="mini-btn" onClick={() => props.onUploadForCompany!(sel.id)} data-testid={`upload-susulan-flag-${sel.id}`} title="Unggah dokumen yang kurang, AI melengkapi data">+ Susulkan dokumen kurang</button>
                )}
              </div>
              {companyFlags.length === 0 ? <div className="rp-sub" style={{ marginTop: 6 }}>Tidak ada isu data.</div> :
                companyFlags.map((f, i) => (
                  <div className="flag-item" key={i}>
                    <div className={"ft sev-" + f.sev}>{f.sev === "red" ? "MERAH" : f.sev === "yellow" ? "KUNING" : "INFO"} · {f.code}</div>
                    <div className="fm">{f.message}</div>
                    {f.source && <div className="fs">{f.source}</div>}
                  </div>
                ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function PersonDetail(props: Props) {
  const { sel, step, cache, onClose } = props;
  const p = PERSONS[sel.id];

  const rows: { cid: string; direct: Frac; eff: Frac; paths: ReturnType<typeof ownershipPaths> }[] = [];
  for (const cid of COMPANY_ORDER) {
    const v = activeVersion(cid, step);
    if (!v) continue;
    const eff = effective(sel.id, cid, cache);
    const direct = directFrac(v, sel.id);
    if (isZero(eff) && isZero(direct)) continue;
    rows.push({ cid, direct, eff, paths: ownershipPaths(sel.id, cid, step) });
  }

  // jabatan
  const jab: string[] = [];
  for (const cid of COMPANY_ORDER) {
    const v = activeVersion(cid, step);
    if (!v) continue;
    for (const [pid, role] of v.officers) if (pid === sel.id) jab.push(`${role} di ${COMPANIES[cid].short}`);
  }

  const cap = personDirectCapital(sel.id, step);

  return (
    <>
      <Header title={p.name} sub={`Data per ${ddmmyyyy(dataStepDate(step))}.`} onClose={onClose} />
      <div className="rp-body">
        <div className="sec">
          <h5>Setoran modal langsung per PT</h5>
          <table className="kv" data-testid="person-capital-table">
            <thead><tr><th>PT</th><th className="num">Lembar</th><th className="num">Nilai setoran</th></tr></thead>
            <tbody>
              {cap.parts.map((pt) => (
                <tr key={pt.companyId} data-testid={`capital-row-${pt.companyId}`}>
                  <td>{COMPANIES[pt.companyId].short}</td>
                  <td className="num">{pt.shares.toLocaleString("id-ID")}</td>
                  <td className="num">{formatRupiah(pt.amount)}</td>
                </tr>
              ))}
              {cap.parts.length === 0 && <tr><td colSpan={3} className="rp-sub">Tidak menyetor modal langsung pada tanggal ini.</td></tr>}
              {cap.parts.length > 0 && (
                <tr style={{ fontWeight: 700, borderTop: "1px solid var(--border)" }} data-testid="capital-total-row">
                  <td>Total</td>
                  <td className="num">{cap.parts.reduce((s, x) => s + x.shares, 0).toLocaleString("id-ID")}</td>
                  <td className="num">{formatRupiah(cap.total)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="sec">
          <h5>Kepemilikan di PT</h5>
          <table className="kv">
            <thead><tr><th>PT</th><th className="num">% langsung</th><th className="num">% efektif</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.cid}>
                  <tr>
                    <td>{COMPANIES[r.cid].short}</td>
                    <td className="num">{isZero(r.direct) ? "—" : toPercentString(r.direct)}</td>
                    <td className="num">{toPercentString(r.eff)}</td>
                  </tr>
                  {r.paths.map((pt, j) => (
                    <tr key={j}><td colSpan={3}>
                      <div className="subpath">
                        {pt.chain.map((id) => PERSONS[id]?.name ?? COMPANIES[id].short).join(" → ")}
                        {": "}
                        {pt.fracs.map((f) => toFractionString(f)).join(" × ")}
                        {" = "}{toPercentString(pt.product)}
                      </div>
                    </td></tr>
                  ))}
                </React.Fragment>
              ))}
              {rows.length === 0 && <tr><td colSpan={3} className="rp-sub">Tidak memiliki saham pada tanggal ini.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="sec">
          <h5>Jabatan</h5>
          {jab.length ? <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "var(--secondary)" }}>{jab.map((j, i) => <li key={i}>{j}</li>)}</ul>
            : <div className="rp-sub">Tidak menjabat pada tanggal ini.</div>}
        </div>

        <div className="sec">
          <h5>Identitas</h5>
          <div className="rp-sub">NIK disimpan terenkripsi dan tidak ditampilkan di prototipe.</div>
          {p.variants.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 12, color: "var(--secondary)" }}>
              Variasi penulisan nama di akta:
              <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{p.variants.map((vv, i) => <li key={i}>{vv}</li>)}</ul>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// tanggal per langkah
function dataStepDate(step: number): string {
  const s = STEPS[step] || STEPS[STEPS.length - 1];
  return s.date;
}
